import React, { createContext, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { useUserContext } from 'store';
import { useAccessHistoryAPI } from 'apiServices/accessHistoryAPI';

import {
  formatISO2DisplayDateString,
  formatISO2DisplayTimeString,
} from 'utils/dateFunctions';
import i18n from 'i18n';
import { useNewConsentModalContext } from 'pages/SharePage/Context/NewConsentModalContext';
import { formatName } from 'utils/AuxiliarFunctions';
import AdminFhirRequests from 'services/AdminFhirRequests';

const initialState = [
  {
    CPF: '',
    name: '',
    purposeOfUse: '',
    date: '',
    hour: '',
    instituicao: '',
  },
];

const AccessHistoryContext = createContext(initialState);

export function AccessHistoryContextProvider({ children }) {
  const [user] = useUserContext();
  const [responseAccessHistory] = useAccessHistoryAPI({
    patientID: user.username,
  });
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { organizationList } = useNewConsentModalContext();

  function getPurposeOfUseText(purpose, instituicao) {
    if (purpose === 'Emergencia') return 'Emergência';
    if (purpose === 'Atendimento' && instituicao) return 'Atendimento';
    if (purpose === 'Atendimento') return 'Consentimento individual';
    if (purpose === 'Federacao') return 'Atendimento na rede de saúde';
    if (!purpose) return '-';
    return purpose;
  }

  async function getOrganizationName(purposeOfUse, instituicao) {
    if (!instituicao) return '-';
    if (purposeOfUse === 'Federacao') {
      const org = await AdminFhirRequests.getOrganizationOnline(
        instituicao,
      ).then((data) => data);
      return org;
    }
    const organization = organizationList?.find(
      (item) => item?.resource?.identifier?.[0]?.value === instituicao,
    );
    return (
      organization?.resource?.alias[0] || organization?.resource?.name || '-'
    );
  }

  async function formatAccessHistory(items) {
    const res = await Promise.all(
      items.map(async (item) => ({
        cpf: item.sujeito.id,
        name: formatName(item.sujeito.nome || ''),
        purposeOfUse: getPurposeOfUseText(
          item.contexto.motivoAcesso,
          item.contexto.instituicao,
        ),
        date: formatISO2DisplayDateString(item.hora, i18n.language),
        hour: formatISO2DisplayTimeString(item.hora),
        instituicao: await getOrganizationName(
          item.contexto.motivoAcesso,
          item.contexto.instituicao,
        ),
      })),
    );
    return res;
  }

  useEffect(() => {
    if (Array.isArray(responseAccessHistory)) {
      setLoading(true);
      (async () => {
        const accessHistory = await formatAccessHistory(responseAccessHistory);
        setHistoryData(accessHistory);
        setLoading(false);
      })();
    }
  }, [responseAccessHistory]);

  const contextValue = { accessHistoryList: historyData, loading };

  return (
    <AccessHistoryContext.Provider value={contextValue}>
      {children}
    </AccessHistoryContext.Provider>
  );
}

export function useAccessHistoryContext() {
  return useContext(AccessHistoryContext);
}

AccessHistoryContextProvider.propTypes = {
  children: PropTypes.element.isRequired,
};
